import { Component, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DynamicTranslateService, LANGUAGES } from '@contler/core/dynamicTranslate';
import { getActualLan } from '@contler/translate/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { StrokedButtonComponent } from '../../components';
import { ButtonComponent } from '../../components/button/button.component';
import { FormFieldComponent } from '../../components/forms/form-field/form-field.component';
import { InputDirective } from '../../directives/input.directive';

@Component({
  selector: 'ctr-translation-modal',
  imports: [
    MatDialogModule,
    ButtonComponent,
    FormFieldComponent,
    InputDirective,
    ReactiveFormsModule,
    TranslateModule,
    StrokedButtonComponent,
  ],
  templateUrl: './translation-modal.component.html',
  styleUrl: './translation-modal.component.scss',
})
export class TranslationModalComponent {
  private readonly dialogRef = inject(MatDialogRef<TranslationModalComponent>);
  private readonly translateService = inject(TranslateService);
  private readonly dynamicTranslateService = inject(DynamicTranslateService);
  readonly data = inject<Record<string, string>>(MAT_DIALOG_DATA);

  form = new FormGroup<Record<string, FormControl<string>>>({});
  languageCodes = signal<string[]>([]);
  currentLanguage = signal<string>('');
  isTranslating = signal<boolean>(false);

  constructor() {
    this.initializeForm();
  }

  /**
   * Initializes the form with controls for each language in the translations object
   * Reorders languages to show current language first
   */
  private initializeForm(): void {
    const defaultData = LANGUAGES.reduce(
      (acc, lang) => {
        acc[lang.prefix] = '';
        return acc;
      },
      {} as Record<string, string>,
    );
    const realData = Object.keys(this.data).length ? this.data : { ...defaultData, ...this.data };
    const codes = Object.keys(realData);
    const currentLang = this.translateService.currentLang?.replace('_', '-') || getActualLan();
    this.currentLanguage.set(currentLang);

    const reorderedCodes = this.reorderLanguages(codes, currentLang);
    this.languageCodes.set(reorderedCodes);

    reorderedCodes.forEach((code) => {
      this.form.addControl(code, new FormControl<string>(this.data[code] || '', { nonNullable: true }));
    });
  }

  /**
   * Reorders language codes to put the current language first
   * @param codes - Array of language codes
   * @param currentLang - Current language code
   * @returns Reordered array with current language first
   */
  private reorderLanguages(codes: string[], currentLang: string): string[] {
    const currentIndex = codes.findIndex((code) => code === currentLang || code.startsWith(currentLang.split('-')[0]));

    if (currentIndex === -1) {
      return codes;
    }

    const reordered = [...codes];
    const [currentCode] = reordered.splice(currentIndex, 1);
    return [currentCode, ...reordered];
  }

  /**
   * Collects all form values and closes the modal with the updated translations
   */
  confirm(): void {
    const updatedTranslations: Record<string, string> = {};

    this.languageCodes().forEach((code) => {
      const control = this.form.get(code);
      if (control) {
        updatedTranslations[code] = control.value;
      }
    });

    this.dialogRef.close(updatedTranslations);
  }

  /**
   * Closes the modal without saving changes
   */
  cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Translates the current language text to all other languages
   * Uses the first language field value as the source text
   */
  async translate(): Promise<void> {
    const currentLangCode = this.currentLanguage();
    const currentControl = this.form.get(currentLangCode);

    if (!currentControl || !currentControl.value.trim()) {
      return;
    }

    const textToTranslate = currentControl.value.trim();
    this.isTranslating.set(true);

    try {
      const translations = await firstValueFrom(this.dynamicTranslateService.translateBasic(textToTranslate));

      translations.forEach(({ language, translation }) => {
        const control = this.form.get(language);
        if (control) {
          control.setValue(translation);
        }
      });
    } catch (error) {
      console.error('Error translating text:', error);
    } finally {
      this.isTranslating.set(false);
    }
  }
}
